// AI Generated Test
import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';

import { SeoRepoService } from '../services/seo-repo.service';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { SessionService } from '@monsido/core/session/session.service';
import { MonLoadingService } from '@monsido/services/loading/services/loading.service';
import { ActiveFeatureService } from '@monsido/services/active-feature/active-feature.service';
import { CrawlsRepoService } from '@client/app/services/api/crawls-repo/crawls-repo.service';
import { PageRepoService } from '@client/app/services/api/page-repo/page-repo.service';
import { SEOLineChartFactory } from '@client/app/blocks/service/charts/seo-line-chart/seo-line-chart.service';
import { ScoresService } from '@client/app/modules/score/services/scores/scores.service';
import { DemoHasAccessService } from '@client/app/modules/demodata/services/demo-has-access.service';
import { PropertiesSumService } from '@client/app/filters/properties-sum.service';
import { ComplianceService } from '@client/app/blocks/service/compliance/compliance.service';
import { PipeService } from '@client/app/filters/pipe-transformation.service';
import {
    ChangesHistoryService,
} from '@client/app/pages/global/changes-history/services/changes-history/changes-history.service';

interface SeoSummaryData {
    compliance_percentage: number;
    pages_count: number;
    pages_with_seo_errors: number;
    seo_errors: number;
    seo_errors_alerts_count: number;
    seo_errors_infos_count: number;
    seo_errors_technicals_count: number;
    seo_errors_warnings_count: number;
}

interface CrawlData {
    seo_errors_alerts_count: number;
    seo_errors_warnings_count: number;
    seo_errors_infos_count: number;
    seo_errors_technical_count: number;
    pages_with_seo_errors_count: number;
    page_count: number;
    created_at: string;
}

interface ChartCard {
    title: string;
    tooltipTitle?: string;
    issues: number;
    change: number;
    changePercentage: number;
}

@Component({
    selector: 'seo-summary',
    templateUrl: './summary.component.html',
    standalone: false,
})
export class SeoSummaryComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();

    loading = true;
    domain: any;
    summaryData: SeoSummaryData | null = null;
    crawlsData: CrawlData[] = [];
    checkpointsData: any[] = [];
    totalPages = 0;
    pageAlerts = 0;
    pageWarnings = 0;
    pageInfos = 0;
    pageTechnicals = 0;
    showHistory = false;
    historyCenterParameters: any = {};
    allowVerticalAverage = false;

    // Chart data
    doughnutData: number[] = [];
    doughnutLabels: string[] = ['', ''];
    doughnutColors: string[] = ['#4dc0bd', '#dcdcdc'];
    doughnutOptions: any = {};

    lineChartLabels: any[] = [];
    lineChartData: any[] = [];
    lineChartSeries: string[] = [];
    lineColors: any[] = [];
    lineOptions: any = {};
    lineOverride: any = [];

    chartCardsList: ChartCard[] = [];

    // Progress and vertical data
    chartProgress = false;
    verticalData: any = null;

    constructor (
        private seoRepoService: SeoRepoService,
        private translateService: TranslateService,
        private sessionService: SessionService,
        private loadingService: MonLoadingService,
        private activeFeatureService: ActiveFeatureService,
        private crawlsRepoService: CrawlsRepoService,
        private pageRepoService: PageRepoService,
        private seoLineChartFactory: SEOLineChartFactory,
        private scoresService: ScoresService,
        private demoHasAccessService: DemoHasAccessService,
        private propertiesSumService: PropertiesSumService,
        private complianceService: ComplianceService,
        private pipeService: PipeService,
        private changesHistoryService: ChangesHistoryService,
    ) {}

    ngOnInit (): void {
        this.domain = this.sessionService.domain;
        this.showHistory = this.activeFeatureService.isFeatureActivePure('history_center_all');
        this.allowVerticalAverage = this.activeFeatureService.isFeatureActive('monsido_score');
        this.historyCenterParameters = this.changesHistoryService.getHistoryCenterParameters('seo');
        this.loadData();
    }

    ngOnDestroy (): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private async loadData (): Promise<void> {
        this.loading = true;
        this.chartProgress = true;
        this.loadingService.show();

        try {
            // Load all data in parallel - matching the original $q.all approach
            const [
                summaryData,
                crawlsData,
                checkpointsData,
                pagesWithAlerts,
                pagesWithWarnings,
                pagesWithInfos,
                pagesWithTechnicals,
            ] = await Promise.all([
                this.getSummary(),
                this.getCrawls(),
                this.getSEOCheckpoints(),
                this.getPages({ seo_issue_alerts: true }),
                this.getPages({ seo_issue_warnings: true }),
                this.getPages({ seo_issue_infos: true }),
                this.getPages({ seo_issue_technicals: true }),
            ]);

            // Set the loaded data
            this.summaryData = summaryData;
            this.crawlsData = crawlsData;
            this.checkpointsData = checkpointsData;

            // Set page counts
            this.totalPages = parseInt(String(summaryData.pages_count));
            this.pageAlerts = parseInt(String(pagesWithAlerts.total));
            this.pageWarnings = parseInt(String(pagesWithWarnings.total));
            this.pageInfos = parseInt(String(pagesWithInfos.total));
            this.pageTechnicals = parseInt(String(pagesWithTechnicals.total));

            // Setup charts and UI
            this.setupChartNotifications(summaryData, crawlsData);
            this.setupDoughnutChart(summaryData, checkpointsData, crawlsData);
            this.setupLineChart(crawlsData.slice().reverse());
            this.setupVerticalData();

        } catch (error) {
            // Error handling - could emit to an error service or set error state
        } finally {
            this.loading = false;
            this.chartProgress = false;
            this.loadingService.hide();
        }
    }

    private async getSummary (): Promise<SeoSummaryData> {
        return this.seoRepoService.getSummary();
    }

    private async getPages (seoParams: any): Promise<any> {
        const params = {
            page: 1,
            page_size: 1,
            ...seoParams,
        };
        return this.pageRepoService.getAll(params);
    }

    private async getSEOCheckpoints (): Promise<any[]> {
        const params: any = {
            page_size: 0,
        };
        return this.seoRepoService.getAll(params);
    }

    private async getCrawls (): Promise<any[]> {
        return this.crawlsRepoService.getAll();
    }

    private setupLineChart (crawls: any[]): void {
        const lineChartService = this.seoLineChartFactory.createSEOLineChart();
        this.lineColors = lineChartService.getColors();
        this.lineChartSeries = lineChartService.getSeries();
        this.lineOptions = lineChartService.getOption();
        this.lineOverride = lineChartService.getOverride();
        this.lineChartLabels = lineChartService.getLabels(crawls);
        this.lineChartData = lineChartService.getData(crawls);
    }

    private setupDoughnutChart (summary: SeoSummaryData, checkpoints: any[], history: any[]): void {
        const percentage = this.complianceService.seoCompliancePercentage(
            summary,
            checkpoints,
            history,
        );
        this.doughnutData = this.complianceService.seoCompliance(
            summary,
            checkpoints,
            history,
        );
        this.doughnutLabels = ['', ''];
        this.doughnutColors = ['#4dc0bd', '#dcdcdc'];

        const vm = this;
        this.doughnutOptions = {
            centerText: {
                percentageLabel: this.pipeService.get('monNumber')(
                    percentage / 100,
                    '0.[00] %',
                ),
                totalLabel: this.translateService.getString('SEO Issues'),
                subLabel: this.translateService.getString('Compliance'),
                subFontSize: 170,
                totalFontSize: 170,
            },
            cutoutPercentage: 80,
            tooltips: {
                enabled: false,
                callbacks: {
                    label: function (tooltipItem, data) {
                        return vm.pipeService.get('monNumber')(data.datasets[0].data[tooltipItem.index]);
                    },
                },
            },
            hover: {
                mode: null,
            },
        };
    }

    private setupChartNotifications (summary: any = {}, crawls: any[]): void {
        const crawlAttrs = [
            'seo_errors_alerts_count',
            'seo_errors_warnings_count',
            'seo_errors_infos_count',
        ];
        const summaryIssues = this.propertiesSumService.sum(
            summary,
            [
                'seo_errors_alerts_count',
                'seo_errors_warnings_count',
                'seo_errors_infos_count',
            ],
        );
        const historyIssues = this.propertiesSumService.sum(
            crawls[crawls.length - 1],
            crawlAttrs,
        );
        const historyPageIssues = this.propertiesSumService.sum(
            crawls[0],
            ['pages_with_seo_errors_count'],
        );
        const summaryPageErrors = this.propertiesSumService.sum(
            summary,
            ['pages_with_seo_errors'],
        );

        this.chartCardsList = [
            {
                title: this.translateService.getString('SEO opportunities found'),
                tooltipTitle: 'Contains high, medium and low opportunities for all pages',
                issues: summaryIssues,
                change:
                        crawls.length > 1 ? summaryIssues - historyIssues : 0,
                changePercentage: this.getPercentageChange(
                    summaryIssues,
                    historyIssues,
                ),
            },
            {
                title: this.translateService.getString('Pages with SEO opportunities'),
                issues: summaryPageErrors,
                change:
                        crawls.length > 1
                            ? summaryPageErrors - historyPageIssues
                            : 0,
                changePercentage: this.getPercentageChange(
                    summaryPageErrors,
                    historyPageIssues,
                ),
            },
        ];
    }

    private getPercentageChange (a: number, b: number): number {
        const difference = Math.abs(a - b);
        const average = (a + b) / 2;

        if (difference >= average) {
            return 100;
        }
        return (difference / average) * 100;
    }

    private setupVerticalData (): void {
        const isDemoAccount = this.demoHasAccessService.isDemoAccount();
        if (isDemoAccount) {
            this.allowVerticalAverage = true;
            this.setVerticalData(this.scoresService.getSEODemoData());
        } else {
            this.allowVerticalAverage = this.activeFeatureService.isFeatureActive('monsido_score');
            if (this.allowVerticalAverage) {
                this.scoresService.getAverageData('seo').then((data) => {
                    this.setVerticalData(data);
                });
            }
        }
    }

    private setVerticalData (data: any): void {
        this.verticalData = {
            verticalAverage: data.score,
            averageTitle: this.scoresService.getDefaultTitle(),
            tooltipText: this.scoresService.getDefaultTooltip() + ' ' + this.scoresService.getVerticalName(data.name),
        };
    }

    hasAllPageCounters (): boolean {
        return this.isNumber(this.totalPages) &&
               this.isNumber(this.pageAlerts) &&
               this.isNumber(this.pageWarnings) &&
               this.isNumber(this.pageInfos) &&
               this.isNumber(this.pageTechnicals);
    }

    private isNumber (val: any): boolean {
        return typeof val === 'number';
    }
}
